import React, { useState } from "react";
import {
  Globe,
  Server,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Clock,
  Github,
  Layers,
  Shield,
  Zap,
} from "lucide-react";

export default function SiteCard({ site }) {
  const [expanded, setExpanded] = useState(false);

  const feUp = site.frontend_status === "up";
  const beUp = site.backend_status === "up";
  const allUp = feUp && beUp;

  const statusColor = allUp ? "green" : "red";
  const statusText = allUp ? "Operational" : "Degraded";

  return (
    <div
      className={`bg-gray-900 border rounded-xl overflow-hidden transition-all ${
        allUp ? "border-gray-800" : "border-red-500/30"
      }`}
    >
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Status indicator */}
            <div
              className={`w-3 h-3 rounded-full ${
                allUp
                  ? "bg-green-500 status-pulse-up"
                  : "bg-red-500 status-pulse-down"
              }`}
            />
            <div>
              <h3 className="text-base font-semibold text-white">
                {site.name}
              </h3>
              <p className="text-xs text-gray-500">{site.description}</p>
            </div>
          </div>
          <span
            className={`text-xs font-medium px-2.5 py-1 rounded-full ${
              allUp
                ? "bg-green-500/10 text-green-400"
                : "bg-red-500/10 text-red-400"
            }`}
          >
            {statusText}
          </span>
        </div>

        {/* Status rows */}
        <div className="space-y-2">
          <StatusRow
            icon={Globe}
            label="Frontend"
            status={site.frontend_status}
            httpCode={site.frontend_http_code}
            responseTime={site.frontend_response_time}
            url={site.frontend_url}
          />
          <StatusRow
            icon={Server}
            label="Backend"
            status={site.backend_status}
            httpCode={site.backend_http_code}
            responseTime={site.backend_response_time}
            url={site.backend_url}
          />
        </div>

        {/* Domain */}
        <div className="mt-4 flex items-center justify-between">
          <a
            href={`https://${site.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-brand-400 hover:text-brand-300 flex items-center gap-1 transition-colors"
          >
            {site.domain}
            <ExternalLink className="w-3 h-3" />
          </a>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-gray-500 hover:text-gray-300 transition-colors"
          >
            {expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-800 px-5 py-4 bg-gray-900/50 space-y-3">
          <DetailRow icon={Layers} label="Stack" value={site.stack} />
          <DetailRow
            icon={Zap}
            label="Backend Port"
            value={site.backend_port}
          />
          <DetailRow
            icon={Server}
            label="PM2 Process"
            value={site.pm2_process}
          />
          <DetailRow
            icon={Shield}
            label="SSL"
            value={site.ssl ? "Active" : "None"}
          />
          {site.github_repo && (
            <DetailRow
              icon={Github}
              label="GitHub"
              value={
                <a
                  href={`https://github.com/${site.github_repo}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-400 hover:text-brand-300 flex items-center gap-1"
                >
                  {site.github_repo}
                  <ExternalLink className="w-3 h-3" />
                </a>
              }
            />
          )}
          {site.last_checked && (
            <DetailRow
              icon={Clock}
              label="Last Checked"
              value={new Date(site.last_checked).toLocaleString()}
            />
          )}
          {site.backend_health_data && (
            <div className="mt-2 bg-gray-800 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Backend Health Data</p>
              <pre className="text-xs text-gray-300 font-mono overflow-x-auto">
                {JSON.stringify(site.backend_health_data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusRow({ icon: Icon, label, status, httpCode, responseTime, url }) {
  const isUp = status === "up";
  return (
    <div className="flex items-center justify-between bg-gray-800/50 rounded-lg px-3 py-2">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-300">{label}</span>
      </div>
      <div className="flex items-center gap-3">
        {responseTime > 0 && (
          <span className="text-xs text-gray-500 font-mono">
            {(responseTime * 1000).toFixed(0)}ms
          </span>
        )}
        {httpCode > 0 && (
          <span
            className={`text-xs font-mono ${
              httpCode === 200 ? "text-green-400" : "text-yellow-400"
            }`}
          >
            {httpCode}
          </span>
        )}
        <span
          className={`text-xs font-semibold px-2 py-0.5 rounded ${
            isUp
              ? "bg-green-500/10 text-green-400"
              : "bg-red-500/10 text-red-400"
          }`}
        >
          {isUp ? "UP" : "DOWN"}
        </span>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="w-4 h-4 text-gray-600 flex-shrink-0" />
      <span className="text-gray-500 w-28 flex-shrink-0">{label}</span>
      <span className="text-gray-300 font-mono text-xs">{value}</span>
    </div>
  );
}
